import React, { useState, useEffect, useCallback, useRef } from 'react';
import { authAxios } from '../utils/api';
import {
    Zap, Play, RefreshCw, TrendingUp, TrendingDown,
    Shield, AlertTriangle, CheckCircle, Clock, BarChart2,
    Activity, Bot, Link, Layers, Target, DollarSign, Plus, X, Search,
    BookOpen, BarChart3, Briefcase,
} from 'lucide-react';
import WeeklyReview from './WeeklyReview';
import SignalAccuracy from './SignalAccuracy';
import Portfolios from './Portfolios';

const API_URL = import.meta.env.VITE_API_URL || '';

const fmt = (n) =>
    n != null ? `₹${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 2 })}` : '—';
const pct = (n) =>
    n != null ? `${Number(n) >= 0 ? '+' : ''}${Number(n).toFixed(2)}%` : '—';

// ── Opportunity type badges ────────────────────────────────────────────────
const TYPE_CFG = {
    ETF_NAV_DIVERGENCE: { label: 'ETF Arb',    bg: '#eff6ff', color: '#1d4ed8', border: '#bfdbfe' },
    PAIRS_DIVERGENCE:   { label: 'Pairs',       bg: '#f5f3ff', color: '#6d28d9', border: '#ddd6fe' },
    BOLLINGER_EXTREME:  { label: 'Mean Rev',    bg: '#fefce8', color: '#92400e', border: '#fde68a' },
};
const SIG_CFG = {
    BUY:  { bg: 'bg-emerald-50',  text: 'text-emerald-700', border: 'border-emerald-200', dot: '#10b981' },
    SELL: { bg: 'bg-rose-50',     text: 'text-rose-700',    border: 'border-rose-200',    dot: '#ef4444' },
    HOLD: { bg: 'bg-amber-50',    text: 'text-amber-700',   border: 'border-amber-200',   dot: '#f59e0b' },
};

// ── Sub-components ─────────────────────────────────────────────────────────
function StatCard({ icon: Icon, label, value, sub, color = 'text-slate-800' }) {
    return (
        <div className="glass-panel p-5 flex items-start gap-4">
            <div className="w-10 h-10 rounded-xl bg-indigo-500/10 flex items-center justify-center flex-shrink-0">
                <Icon size={18} className="text-indigo-500" />
            </div>
            <div>
                <div className="text-xs text-slate-500 font-semibold uppercase tracking-wider mb-1">{label}</div>
                <div className={`text-xl font-bold font-mono ${color}`}>{value}</div>
                {sub && <div className="text-xs text-slate-400 mt-0.5">{sub}</div>}
            </div>
        </div>
    );
}

function OpportunityCard({ opp, onAnalyse }) {
    const cfg  = TYPE_CFG[opp.type] || TYPE_CFG.BOLLINGER_EXTREME;
    const isUp = (opp.recommended_action || '').toUpperCase().startsWith('BUY');
    const confPct = Math.round((opp.confidence || 0) * 100);

    return (
        <div className="glass-panel p-5 hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-base font-bold text-slate-800 font-mono">{opp.ticker}</span>
                    <span style={{
                        fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 6,
                        background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`,
                    }}>{cfg.label}</span>
                    {opp.direction && (
                        <span style={{
                            fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 6,
                            background: opp.direction === 'OVERSOLD' ? '#f0fdf4' : '#fef2f2',
                            color: opp.direction === 'OVERSOLD' ? '#15803d' : '#dc2626',
                            border: `1px solid ${opp.direction === 'OVERSOLD' ? '#bbf7d0' : '#fecaca'}`,
                        }}>{opp.direction}</span>
                    )}
                </div>
                <div className="text-right flex-shrink-0 ml-2">
                    <div className={`text-sm font-bold ${isUp ? 'text-emerald-600' : 'text-rose-500'}`}>
                        {isUp ? <TrendingUp size={14} className="inline mr-1" /> : <TrendingDown size={14} className="inline mr-1" />}
                        {opp.expected_profit_pct != null ? `+${Number(opp.expected_profit_pct).toFixed(2)}%` : '—'}
                    </div>
                    <div className="text-[10px] text-slate-400 mt-0.5">expected profit</div>
                </div>
            </div>

            <p className="text-xs text-slate-500 leading-relaxed mb-3">{opp.reasoning}</p>

            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    {/* Confidence bar */}
                    <div className="flex items-center gap-1.5">
                        <div className="w-20 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                            <div className="h-full rounded-full bg-indigo-500 transition-all"
                                 style={{ width: `${confPct}%` }} />
                        </div>
                        <span className="text-[10px] font-mono font-bold text-slate-500">{confPct}%</span>
                    </div>
                    {/* Key metric */}
                    {opp.z_score != null && (
                        <span className="text-[10px] font-mono text-purple-600 font-bold">
                            z={opp.z_score}σ
                        </span>
                    )}
                    {opp.premium_pct != null && (
                        <span className="text-[10px] font-mono text-blue-600 font-bold">
                            {opp.premium_pct > 0 ? '+' : ''}{opp.premium_pct}%
                        </span>
                    )}
                </div>
                <div className="text-xs font-bold text-indigo-600 bg-indigo-50 border border-indigo-100 px-2 py-0.5 rounded">
                    {opp.recommended_action}
                </div>
            </div>
        </div>
    );
}

function TradeRow({ trade }) {
    const isUp = trade.side === 'BUY';
    return (
        <div className="flex items-center gap-3 py-2.5 border-b border-slate-50 last:border-0">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${isUp ? 'bg-emerald-50' : 'bg-rose-50'}`}>
                {isUp ? <TrendingUp size={14} className="text-emerald-600" />
                      : <TrendingDown size={14} className="text-rose-500" />}
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                    <span className="text-sm font-bold text-slate-800">{trade.ticker}</span>
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${isUp ? 'text-emerald-700 bg-emerald-50' : 'text-rose-700 bg-rose-50'}`}>
                        {trade.side}
                    </span>
                </div>
                <div className="text-xs text-slate-400 mt-0.5">
                    {trade.quantity}× @ {fmt(trade.execution_price)}
                </div>
            </div>
            <div className="text-right">
                <div className="text-sm font-bold text-slate-800 font-mono">{fmt(trade.total_value)}</div>
                <div className="text-[10px] text-slate-400">{trade.status || 'EXECUTED'}</div>
            </div>
        </div>
    );
}

// ── Main component ─────────────────────────────────────────────────────────
// ── Risk settings field definitions ────────────────────────────────────────
const RISK_FIELDS = [
    { key: 'max_risk_per_trade',     label: 'Max risk per trade',      unit: '%',  min: 0.1,  max: 5,     step: 0.1,  tip: 'Portfolio % at risk on each trade (ATR-based sizing)' },
    { key: 'max_single_stock_pct',   label: 'Max single stock',        unit: '%',  min: 5,    max: 50,    step: 1,    tip: 'Max % of portfolio in any one stock' },
    { key: 'max_portfolio_heat',     label: 'Max portfolio deployed',  unit: '%',  min: 20,   max: 90,    step: 5,    tip: 'Max % of total portfolio in open positions' },
    { key: 'max_open_positions',     label: 'Max open positions',      unit: '',   min: 1,    max: 20,    step: 1,    tip: 'Hard cap on simultaneous open trades' },
    { key: 'daily_loss_limit',       label: 'Daily loss halt',         unit: '%',  min: 1,    max: 20,    step: 0.5,  tip: 'Stop new trades if portfolio drops this much today' },
    { key: 'drawdown_pause',         label: 'Drawdown pause',          unit: '%',  min: 5,    max: 40,    step: 1,    tip: 'Pause strategy if portfolio falls this % from peak' },
    { key: 'min_confidence',         label: 'Min AI confidence',       unit: '%',  min: 50,   max: 95,    step: 1,    tip: 'Minimum confidence required before executing a trade' },
    { key: 'fii_selling_block_cr',   label: 'FII selling block',       unit: '₹cr', min: 500, max: 10000, step: 100,  tip: 'Block new longs if FII sold more than this today' },
    { key: 'india_vix_limit',        label: 'India VIX limit',         unit: '',   min: 15,   max: 40,    step: 0.5,  tip: 'No new longs if India VIX is above this level' },
    { key: 'consecutive_loss_limit', label: 'Consecutive loss limit',  unit: '',   min: 1,    max: 10,    step: 1,    tip: 'Switch to HOLD-only after this many losses in a row' },
    { key: 'earnings_blackout_days', label: 'Earnings blackout',       unit: 'days', min: 0,  max: 5,     step: 1,    tip: 'No entry within this many days before earnings' },
];

function RiskSettingsPanel({ onClose }) {
    const [settings, setSettings]   = useState(null);
    const [defaults, setDefaults]   = useState({});
    const [draft, setDraft]         = useState({});
    const [saving, setSaving]       = useState(false);
    const [saved, setSaved]         = useState(false);
    const [loadErr, setLoadErr]     = useState('');

    useEffect(() => {
        authAxios.get(`${API_URL}/api/trading/risk-settings`)
            .then(r => {
                setSettings(r.data.settings);
                setDefaults(r.data.defaults);
                setDraft(r.data.settings);
            })
            .catch(() => setLoadErr('Failed to load settings'));
    }, []);

    const handleChange = (key, val) => {
        setDraft(d => ({ ...d, [key]: Number(val) }));
        setSaved(false);
    };

    const handleReset = (key) => {
        setDraft(d => ({ ...d, [key]: defaults[key] }));
        setSaved(false);
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const res = await authAxios.put(`${API_URL}/api/trading/risk-settings`, draft);
            setSettings(res.data.settings);
            setDraft(res.data.settings);
            setSaved(true);
        } catch {
            setLoadErr('Save failed — check values and retry');
        } finally {
            setSaving(false);
        }
    };

    if (loadErr) return (
        <div className="glass-panel p-6 text-red-500 text-sm">{loadErr}</div>
    );
    if (!settings) return (
        <div className="glass-panel p-6 text-slate-400 text-sm animate-pulse">Loading risk settings…</div>
    );

    return (
        <div className="glass-panel p-6 space-y-5">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-base font-semibold text-slate-800 flex items-center gap-2">
                        <Shield size={16} className="text-indigo-500" /> Risk Rules
                    </h2>
                    <p className="text-xs text-slate-400 mt-0.5">
                        All rules are enforced automatically before every trade. AI cannot override them.
                    </p>
                </div>
                {onClose && (
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
                        <X size={16} />
                    </button>
                )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {RISK_FIELDS.map(f => {
                    const current  = draft[f.key] ?? defaults[f.key];
                    const isChanged = current !== defaults[f.key];
                    return (
                        <div key={f.key} className="space-y-1">
                            <div className="flex items-center justify-between">
                                <label className="text-xs font-medium text-slate-600" title={f.tip}>
                                    {f.label}
                                    {f.unit && <span className="text-slate-400 ml-1">({f.unit})</span>}
                                </label>
                                {isChanged && (
                                    <button
                                        onClick={() => handleReset(f.key)}
                                        className="text-[10px] text-indigo-500 hover:underline"
                                    >
                                        Reset to {defaults[f.key]}
                                    </button>
                                )}
                            </div>
                            <div className="flex items-center gap-2">
                                <input
                                    type="range"
                                    min={f.min} max={f.max} step={f.step}
                                    value={current}
                                    onChange={e => handleChange(f.key, e.target.value)}
                                    className="flex-1 accent-indigo-500"
                                />
                                <span className={`text-xs font-mono w-12 text-right ${isChanged ? 'text-indigo-600 font-semibold' : 'text-slate-500'}`}>
                                    {current}{f.unit === '%' ? '%' : ''}
                                </span>
                            </div>
                            <p className="text-[10px] text-slate-400">{f.tip}</p>
                        </div>
                    );
                })}
            </div>

            <div className="flex items-center gap-3 pt-2 border-t border-slate-100">
                <button
                    onClick={handleSave}
                    disabled={saving}
                    className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
                >
                    {saving ? 'Saving…' : 'Save Rules'}
                </button>
                {saved && <span className="text-xs text-emerald-600 font-medium">✓ Saved</span>}
                <span className="text-xs text-slate-400 ml-auto">
                    Changes apply to the next autonomous session
                </span>
            </div>
        </div>
    );
}

export default function AutoTrading() {
    const [opportunities, setOpportunities]   = useState([]);
    const [oppLoading, setOppLoading]         = useState(false);
    const [session, setSession]               = useState(null);
    const [sessionRunning, setSessionRunning] = useState(false);
    const [brokerStatus, setBrokerStatus]     = useState(null);
    const [proposals, setProposals]           = useState([]);
    const [actingId, setActingId]             = useState(null);
    const [error, setError]                   = useState('');
    const [selectedTickers, setSelectedTickers] = useState([]);
    const [tickerInput, setTickerInput]         = useState('');
    const [suggestions, setSuggestions]         = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [suggLoading, setSuggLoading]         = useState(false);
    const [activeSugg, setActiveSugg]           = useState(-1);
    const [showRiskSettings, setShowRiskSettings] = useState(false);
    const searchTimeout                         = useRef(null);
    const pickerRef                             = useRef(null);

    // ── Close dropdown on outside click ─────────────────────────────────
    useEffect(() => {
        const handler = (e) => {
            if (pickerRef.current && !pickerRef.current.contains(e.target)) {
                setShowSuggestions(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    // ── Ticker picker helpers ────────────────────────────────────────────
    const addTicker = (raw) => {
        const parts = raw.toUpperCase().split(/[\s,;]+/).map(t => t.trim()).filter(Boolean);
        setSelectedTickers(prev => {
            const existing = new Set(prev);
            const toAdd = parts.filter(t => t && !existing.has(t));
            return [...prev, ...toAdd];
        });
        setTickerInput('');
        setSuggestions([]);
        setShowSuggestions(false);
        setActiveSugg(-1);
    };

    const removeTicker = (t) => setSelectedTickers(prev => prev.filter(x => x !== t));

    const handleTickerInput = (e) => {
        const val = e.target.value.toUpperCase();
        setTickerInput(val);
        setActiveSugg(-1);
        clearTimeout(searchTimeout.current);
        if (val.length < 2) {
            setSuggestions([]);
            setShowSuggestions(false);
            return;
        }
        setSuggLoading(true);
        setShowSuggestions(true);
        searchTimeout.current = setTimeout(async () => {
            try {
                const res = await authAxios.get(`${API_URL}/api/stocks/search?q=${encodeURIComponent(val)}`);
                setSuggestions(res.data.results || []);
            } catch {
                setSuggestions([]);
            } finally {
                setSuggLoading(false);
            }
        }, 250);
    };

    const handleTickerKey = (e) => {
        if (showSuggestions && suggestions.length > 0) {
            if (e.key === 'ArrowDown') { e.preventDefault(); setActiveSugg(i => Math.min(i + 1, suggestions.length - 1)); return; }
            if (e.key === 'ArrowUp')   { e.preventDefault(); setActiveSugg(i => Math.max(i - 1, -1)); return; }
            if ((e.key === 'Enter' || e.key === 'Tab') && activeSugg >= 0) {
                e.preventDefault();
                addTicker(suggestions[activeSugg].symbol || suggestions[activeSugg].ticker || suggestions[activeSugg]);
                return;
            }
            if (e.key === 'Escape') { setShowSuggestions(false); return; }
        }
        if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            if (tickerInput.trim()) addTicker(tickerInput);
        } else if (e.key === 'Backspace' && !tickerInput && selectedTickers.length > 0) {
            setSelectedTickers(prev => prev.slice(0, -1));
        }
    };

    // ── Fetch helpers ────────────────────────────────────────────────────
    const fetchOpportunities = useCallback(async (tickers) => {
        setOppLoading(true);
        try {
            const tickerList = tickers ?? selectedTickers;
            const params = tickerList.length > 0 ? `?tickers=${tickerList.join(',')}` : '';
            const res = await authAxios.get(`${API_URL}/api/auto-trading/opportunities${params}`);
            setOpportunities(res.data.opportunities || []);
        } catch (e) {
            setError('Could not load opportunities: ' + (e.response?.data?.error || e.message));
        } finally {
            setOppLoading(false);
        }
    }, [selectedTickers]);

    const fetchSession = useCallback(async () => {
        try {
            const res = await authAxios.get(`${API_URL}/api/auto-trading/session/active`);
            setSession(res.data);
        } catch { /* silent */ }
    }, []);

    const fetchBroker = useCallback(async () => {
        try {
            const res = await authAxios.get(`${API_URL}/api/auto-trading/broker/status`);
            setBrokerStatus(res.data);
        } catch { /* silent */ }
    }, []);

    const fetchProposals = useCallback(async () => {
        try {
            const res = await authAxios.get(`${API_URL}/api/auto-trading/proposals`);
            setProposals(res.data.proposals || []);
        } catch { /* silent */ }
    }, []);

    useEffect(() => {
        fetchSession();
        fetchBroker();
        fetchProposals();
        // Poll for new proposals while the page is open (agent sessions run async)
        const t = setInterval(fetchProposals, 15000);
        return () => clearInterval(t);
    }, [fetchProposals]);

    // ── Approve / reject a live-order proposal ─────────────────────────────
    const actOnProposal = async (id, action) => {
        setActingId(id);
        setError('');
        try {
            const res = await authAxios.post(
                `${API_URL}/api/auto-trading/proposals/${id}/${action}`, {}, { timeout: 60000 }
            );
            if (res.data.error) setError(res.data.error);
            await fetchProposals();
        } catch (e) {
            setError(e.response?.data?.error || `Could not ${action} proposal`);
        } finally {
            setActingId(null);
        }
    };

    // ── Complete broker link when Kite redirects back with a request_token ──
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const requestToken = params.get('broker_request_token');
        if (params.get('broker') === 'error') {
            setError('Broker login was cancelled or failed. Please try again.');
            window.history.replaceState({}, '', window.location.pathname);
            return;
        }
        if (!requestToken) return;
        (async () => {
            try {
                const res = await authAxios.post(`${API_URL}/api/auto-trading/broker/connect`, {
                    request_token: requestToken,
                });
                setBrokerStatus(res.data);
            } catch (e) {
                setError(e.response?.data?.error || 'Broker link failed');
            } finally {
                // Strip the token from the URL so it isn't re-used or bookmarked
                window.history.replaceState({}, '', window.location.pathname);
            }
        })();
    }, []);

    // ── Run session ──────────────────────────────────────────────────────
    const runSession = async () => {
        setSessionRunning(true);
        setError('');
        try {
            const res = await authAxios.post(
                `${API_URL}/api/auto-trading/run`,
                { tickers: selectedTickers },
                { timeout: 180000 }
            );
            setSession(res.data);
            // Refresh opportunities panel with same tickers after session completes
            fetchOpportunities();
        } catch (e) {
            setError('Session failed: ' + (e.response?.data?.error || e.message));
        } finally {
            setSessionRunning(false);
        }
    };

    // ── Broker connect ───────────────────────────────────────────────────
    // Navigate in the SAME window so Zerodha's redirect lands back on our app
    // (with ?broker_request_token=...), which the effect above completes.
    const connectBroker = async () => {
        try {
            const res = await authAxios.get(`${API_URL}/api/auto-trading/broker/login-url`);
            if (res.data.login_url) window.location.href = res.data.login_url;
        } catch (e) {
            setError(e.response?.data?.error || 'Broker connection error');
        }
    };

    // ── Toggle the live-trading kill-switch ────────────────────────────────
    const toggleLiveTrading = async () => {
        const next = !brokerStatus?.settings?.live_trading_enabled;
        try {
            await authAxios.put(`${API_URL}/api/auto-trading/broker/settings`, {
                live_trading_enabled: next,
            });
            fetchBroker();
        } catch (e) {
            setError(e.response?.data?.error || 'Could not update live-trading setting');
        }
    };

    // ── Derived stats ────────────────────────────────────────────────────
    const totalCapitalDeployed = session?.total_capital_deployed || 0;
    const tradesExecuted       = session?.trades_executed || 0;
    const oppsFound            = session?.opportunities_found || 0;

    return (
        <div className="p-6 space-y-6 max-w-6xl mx-auto">

            {/* ── Header ── */}
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
                        <Zap size={22} className="text-indigo-500" />
                        Auto Trading
                    </h1>
                    <p className="text-sm text-slate-500 mt-1">
                        Autonomous Claude agent — scans for ETF arbitrage, pairs divergence &amp; mean-reversion, then executes paper trades.
                    </p>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                    {/* Broker badge */}
                    {brokerStatus && (
                        <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-bold ${
                            brokerStatus.needs_relink
                                ? 'bg-amber-50 border-amber-200 text-amber-700'
                                : brokerStatus.mode === 'live'
                                    ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                                    : 'bg-slate-50 border-slate-200 text-slate-500'
                        }`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${
                                brokerStatus.needs_relink ? 'bg-amber-500'
                                : brokerStatus.mode === 'live' ? 'bg-emerald-500' : 'bg-slate-400'
                            }`} />
                            {brokerStatus.needs_relink ? 'Re-link Kite'
                                : brokerStatus.mode === 'live' ? 'Live · Zerodha' : 'Paper Mode'}
                        </div>
                    )}
                    <button
                        onClick={() => fetchOpportunities()}
                        disabled={oppLoading}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 text-slate-600 text-xs font-semibold hover:bg-slate-50 disabled:opacity-50 transition"
                    >
                        <RefreshCw size={12} className={oppLoading ? 'animate-spin' : ''} />
                        {selectedTickers.length > 0 ? `Scan (${selectedTickers.length})` : 'Scan Watchlist'}
                    </button>
                    <button
                        onClick={runSession}
                        disabled={sessionRunning}
                        className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold text-white disabled:opacity-60 transition"
                        style={{
                            background: sessionRunning ? '#94a3b8' : 'linear-gradient(135deg,#4f46e5,#7c3aed)',
                            cursor: sessionRunning ? 'not-allowed' : 'pointer',
                        }}
                    >
                        {sessionRunning ? (
                            <>
                                <div style={{ width: 14, height: 14, borderRadius: '50%', border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', animation: 'spin 0.7s linear infinite' }} />
                                Agent Running…
                            </>
                        ) : (
                            <><Play size={14} /> Run Agent</>
                        )}
                    </button>
                </div>
            </div>

            <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>

            {/* ── Error banner ── */}
            {error && (
                <div className="p-3 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 text-sm flex items-center gap-2">
                    <AlertTriangle size={15} /> {error}
                    <button onClick={() => setError('')} className="ml-auto text-rose-400 hover:text-rose-600">✕</button>
                </div>
            )}

            {/* ── Pending approvals (live mode) ── */}
            {proposals.length > 0 && (
                <div className="glass-panel p-5 border-2 border-amber-200">
                    <div className="flex items-center gap-2 mb-4">
                        <Bot size={16} className="text-amber-500" />
                        <span className="text-sm font-bold text-slate-800">
                            {proposals.length} trade{proposals.length > 1 ? 's' : ''} awaiting your approval
                        </span>
                        <span className="text-[11px] text-slate-400 ml-auto">Real orders — nothing is placed until you approve.</span>
                    </div>
                    <div className="space-y-3">
                        {proposals.map((p) => (
                            <div key={p.id} className="flex items-center gap-3 flex-wrap p-3 rounded-xl bg-white border border-slate-200">
                                <span className={`px-2 py-1 rounded-lg text-xs font-bold ${p.side === 'BUY' ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`}>
                                    {p.side}
                                </span>
                                <span className="font-bold text-slate-800">{p.ticker}</span>
                                <span className="text-sm text-slate-500">{p.quantity}× @ {fmt(p.price)}</span>
                                <span className="text-sm font-semibold text-slate-700">{fmt((p.price || 0) * (p.quantity || 0))}</span>
                                <span className="text-xs text-slate-400 flex-1 min-w-[160px] truncate" title={p.reasoning}>{p.reasoning}</span>
                                <button
                                    onClick={() => actOnProposal(p.id, 'approve')}
                                    disabled={actingId === p.id}
                                    className="px-3 py-1.5 rounded-lg text-xs font-bold text-white bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 transition"
                                >
                                    {actingId === p.id ? '…' : '✅ Approve'}
                                </button>
                                <button
                                    onClick={() => actOnProposal(p.id, 'reject')}
                                    disabled={actingId === p.id}
                                    className="px-3 py-1.5 rounded-lg text-xs font-bold text-slate-600 bg-slate-100 hover:bg-slate-200 disabled:opacity-50 transition"
                                >
                                    Reject
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* ── Stats row ── */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatCard icon={Target}     label="Opportunities"     value={opportunities.length}     sub="detected now" />
                <StatCard icon={Play}       label="Trades This Session" value={tradesExecuted}          sub="executed by agent" />
                <StatCard icon={DollarSign} label="Capital Deployed"  value={fmt(totalCapitalDeployed)} sub="this session" />
                <StatCard icon={Layers}     label="Signals Scanned"   value={`${oppsFound || opportunities.length}`} sub="opportunities found" />
            </div>

            {/* ── Stock Picker ── */}
            <div className="glass-panel p-4">
                <div className="flex items-center gap-2 mb-3">
                    <Search size={13} className="text-indigo-400" />
                    <span className="text-xs font-bold text-slate-700 uppercase tracking-wider">Scan Specific Stocks</span>
                    <span className="text-[10px] text-slate-400 ml-1">— search by name or ticker</span>
                    {selectedTickers.length > 0 && (
                        <button
                            onClick={() => setSelectedTickers([])}
                            className="ml-auto text-[10px] text-slate-400 hover:text-rose-500 transition"
                        >
                            Clear all
                        </button>
                    )}
                </div>

                <div ref={pickerRef} className="relative">
                    <div className="flex flex-wrap gap-2 items-center min-h-[36px] bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 focus-within:border-indigo-400 focus-within:ring-1 focus-within:ring-indigo-200 transition">
                        {selectedTickers.map(t => (
                            <span key={t} className="flex items-center gap-1 text-xs font-bold bg-indigo-100 text-indigo-700 border border-indigo-200 px-2 py-0.5 rounded-lg">
                                {t}
                                <button onClick={() => removeTicker(t)} className="text-indigo-400 hover:text-indigo-700 ml-0.5 leading-none">
                                    <X size={10} />
                                </button>
                            </span>
                        ))}
                        <input
                            type="text"
                            value={tickerInput}
                            onChange={handleTickerInput}
                            onKeyDown={handleTickerKey}
                            onFocus={() => tickerInput.length >= 2 && setShowSuggestions(true)}
                            placeholder={selectedTickers.length === 0 ? 'Search: TCS, Infosys, Reliance…' : 'Add more…'}
                            className="flex-1 min-w-[160px] bg-transparent text-xs text-slate-700 placeholder:text-slate-400 outline-none"
                            autoComplete="off"
                        />
                        {suggLoading && (
                            <div style={{ width: 12, height: 12, borderRadius: '50%', border: '2px solid #e2e8f0', borderTopColor: '#6366f1', animation: 'spin 0.7s linear infinite', flexShrink: 0 }} />
                        )}
                    </div>

                    {/* Autocomplete dropdown */}
                    {showSuggestions && suggestions.length > 0 && (
                        <div className="absolute z-50 left-0 right-0 top-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden">
                            {suggestions.slice(0, 8).map((s, i) => {
                                const sym  = s.symbol || s.ticker || s;
                                const name = s.name || s.company_name || '';
                                const isActive = i === activeSugg;
                                return (
                                    <button
                                        key={sym}
                                        onMouseDown={(e) => { e.preventDefault(); addTicker(sym); }}
                                        className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition ${isActive ? 'bg-indigo-50' : 'hover:bg-slate-50'}`}
                                    >
                                        <span className="text-xs font-bold text-indigo-700 font-mono w-24 flex-shrink-0">{sym}</span>
                                        {name && <span className="text-xs text-slate-500 truncate">{name}</span>}
                                    </button>
                                );
                            })}
                        </div>
                    )}
                    {showSuggestions && !suggLoading && tickerInput.length >= 2 && suggestions.length === 0 && (
                        <div className="absolute z-50 left-0 right-0 top-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg px-4 py-3 text-xs text-slate-400">
                            No matches for "{tickerInput}" — press Enter to add anyway
                        </div>
                    )}
                </div>

                <div className="flex items-center gap-2 mt-3 flex-wrap">
                    {/* Quick-add popular tickers */}
                    <span className="text-[10px] text-slate-400 font-semibold">Quick add:</span>
                    {['RELIANCE', 'TCS', 'INFY', 'HDFCBANK', 'ICICIBANK', 'NIFTYBEES', 'GOLDBEES', 'BANKBEES'].map(t => (
                        <button
                            key={t}
                            onClick={() => addTicker(t)}
                            disabled={selectedTickers.includes(t)}
                            className="text-[10px] font-bold px-2 py-0.5 rounded border transition disabled:opacity-30 disabled:cursor-not-allowed"
                            style={{ background: selectedTickers.includes(t) ? '#e0e7ff' : '#f8fafc', color: '#4f46e5', borderColor: '#c7d2fe' }}
                        >
                            + {t}
                        </button>
                    ))}
                    <button
                        onClick={() => fetchOpportunities()}
                        disabled={oppLoading}
                        className="ml-auto flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-xs font-bold text-white disabled:opacity-60 transition"
                        style={{ background: oppLoading ? '#94a3b8' : 'linear-gradient(135deg,#4f46e5,#6d28d9)' }}
                    >
                        <Search size={11} />
                        {selectedTickers.length > 0 ? `Scan ${selectedTickers.length} stock${selectedTickers.length > 1 ? 's' : ''}` : 'Scan Watchlist'}
                    </button>
                </div>
            </div>

            {/* ── Main grid ── */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

                {/* Left: Opportunities (2 cols) */}
                <div className="lg:col-span-2 space-y-4">
                    <div className="flex items-center justify-between">
                        <h2 className="text-sm font-bold text-slate-700 uppercase tracking-wider flex items-center gap-2">
                            <Activity size={14} className="text-indigo-400" />
                            Market Opportunities
                        </h2>
                        <span className="text-xs text-slate-400">{opportunities.length} found</span>
                    </div>

                    {oppLoading && (
                        <div className="glass-panel p-8 text-center text-slate-400 text-sm">
                            <div style={{ width: 28, height: 28, borderRadius: '50%', border: '3px solid #e2e8f0', borderTopColor: '#6366f1', animation: 'spin 0.8s linear infinite', margin: '0 auto 12px' }} />
                            Scanning {Object.keys({ETF_NAV_DIVERGENCE: 1, PAIRS_DIVERGENCE: 1, BOLLINGER_EXTREME: 1}).length} detectors…
                        </div>
                    )}

                    {!oppLoading && opportunities.length === 0 && (
                        <div className="glass-panel p-8 text-center">
                            <BarChart2 size={28} className="text-slate-300 mx-auto mb-3" />
                            <p className="text-sm font-semibold text-slate-500">No opportunities detected</p>
                            <p className="text-xs text-slate-400 mt-1 leading-relaxed">
                                Type any NSE tickers above (e.g. TCS, INFY, RELIANCE) and click Scan.<br />
                                Detects ETF arbitrage, pairs divergence &amp; Bollinger Band extremes.
                            </p>
                        </div>
                    )}

                    {opportunities.map((opp, i) => (
                        <OpportunityCard key={i} opp={opp} />
                    ))}
                </div>

                {/* Right: Agent panel + Broker */}
                <div className="space-y-4">

                    {/* Agent Session */}
                    <div className="glass-panel p-5">
                        <div className="flex items-center gap-2 mb-4">
                            <Bot size={15} className="text-indigo-400" />
                            <span className="text-xs font-bold text-slate-700 uppercase tracking-wider">Agent Session</span>
                            {session?.status === 'running' && (
                                <span className="ml-auto text-[10px] font-bold text-indigo-600 bg-indigo-50 border border-indigo-100 px-2 py-0.5 rounded animate-pulse">
                                    LIVE
                                </span>
                            )}
                            {session?.status === 'complete' && (
                                <span className="ml-auto text-[10px] font-bold text-emerald-600 bg-emerald-50 border border-emerald-100 px-2 py-0.5 rounded">
                                    DONE
                                </span>
                            )}
                        </div>

                        {!session || session.status === 'idle' ? (
                            <div className="text-center py-4 text-slate-400">
                                <Clock size={24} className="mx-auto mb-2 text-slate-300" />
                                <p className="text-xs">No session yet</p>
                                <p className="text-[11px] text-slate-300 mt-1">Click <strong>Run Agent</strong> to start</p>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {/* Session meta */}
                                <div className="flex gap-3 text-xs">
                                    <div className="flex-1 bg-slate-50 rounded-lg p-2 text-center">
                                        <div className="font-bold text-slate-800 text-base">{session.trades_executed ?? 0}</div>
                                        <div className="text-slate-400">Trades</div>
                                    </div>
                                    <div className="flex-1 bg-slate-50 rounded-lg p-2 text-center">
                                        <div className="font-bold text-slate-800 text-base">{session.opportunities_found ?? 0}</div>
                                        <div className="text-slate-400">Opps</div>
                                    </div>
                                    <div className="flex-1 bg-slate-50 rounded-lg p-2 text-center">
                                        <div className="font-bold text-indigo-600 text-xs">{session.model_used?.includes('haiku') ? 'Haiku' : 'Sonnet'}</div>
                                        <div className="text-slate-400">Model</div>
                                    </div>
                                </div>

                                {/* Reasoning */}
                                {session.reasoning && (
                                    <div className="bg-indigo-50/50 border border-indigo-100 rounded-lg p-3">
                                        <div className="text-[10px] font-bold text-indigo-600 uppercase tracking-wider mb-1">Agent Reasoning</div>
                                        <p className="text-xs text-slate-600 leading-relaxed">{session.reasoning}</p>
                                    </div>
                                )}

                                {/* Market conditions */}
                                {session.market_conditions && (
                                    <div className="text-xs text-slate-500">
                                        <span className="font-semibold text-slate-600">Market: </span>
                                        {session.market_conditions}
                                    </div>
                                )}

                                {/* Trades list */}
                                {session.trades?.length > 0 && (
                                    <div>
                                        <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Executed Trades</div>
                                        {session.trades.map((t, i) => <TradeRow key={i} trade={t} />)}
                                    </div>
                                )}

                                {/* Skipped */}
                                {session.skipped_trades && (
                                    <div className="text-[10px] text-slate-400 italic leading-relaxed">
                                        <span className="font-semibold not-italic text-slate-500">Skipped: </span>
                                        {session.skipped_trades}
                                    </div>
                                )}

                                {session.generated_at && (
                                    <div className="text-[10px] text-slate-300 text-right">
                                        {new Date(session.generated_at).toLocaleString('en-IN', {
                                            day: 'numeric', month: 'short',
                                            hour: '2-digit', minute: '2-digit',
                                        })}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Broker Connection */}
                    <div className="glass-panel p-5">
                        <div className="flex items-center gap-2 mb-4">
                            <Link size={14} className="text-slate-400" />
                            <span className="text-xs font-bold text-slate-700 uppercase tracking-wider">Broker</span>
                        </div>

                        {brokerStatus ? (
                            <div className="space-y-3">
                                <div className="flex items-center justify-between text-sm">
                                    <span className="text-slate-500">Status</span>
                                    <span className={`font-bold ${brokerStatus.connected ? 'text-emerald-600' : 'text-slate-400'}`}>
                                        {brokerStatus.connected ? <><CheckCircle size={12} className="inline mr-1" />Connected</> : 'Not connected'}
                                    </span>
                                </div>
                                <div className="flex items-center justify-between text-sm">
                                    <span className="text-slate-500">Mode</span>
                                    <span className={`font-bold ${brokerStatus.mode === 'live' ? 'text-emerald-600' : 'text-amber-500'}`}>
                                        {brokerStatus.mode === 'live' ? '🟢 Live' : '📄 Paper'}
                                    </span>
                                </div>
                                <div className="flex items-center justify-between text-sm">
                                    <span className="text-slate-500">Broker</span>
                                    <span className="font-bold text-slate-700 capitalize">{brokerStatus.broker}</span>
                                </div>

                                {/* No live broker configured */}
                                {brokerStatus.broker === 'paper' && (
                                    <p className="text-[10px] text-slate-400 text-center mt-3 pt-3 border-t border-slate-100 leading-relaxed">
                                        Set ZERODHA_API_KEY + ZERODHA_API_SECRET<br />
                                        in .env to enable live trading
                                    </p>
                                )}

                                {/* Zerodha configured but not connected (or token expired) */}
                                {brokerStatus.broker === 'zerodha' && !brokerStatus.connected && (
                                    <div className="mt-3 pt-3 border-t border-slate-100">
                                        {brokerStatus.needs_relink && (
                                            <p className="text-[11px] text-amber-600 font-medium text-center mb-2 flex items-center justify-center gap-1">
                                                <AlertTriangle size={11} /> Daily token expired — re-link to continue
                                            </p>
                                        )}
                                        <button
                                            onClick={connectBroker}
                                            className="w-full py-2 rounded-lg text-xs font-bold text-white transition"
                                            style={{ background: 'linear-gradient(135deg,#1e40af,#3730a3)' }}
                                        >
                                            {brokerStatus.needs_relink ? 'Re-link Zerodha Kite' : 'Connect Zerodha Kite'}
                                        </button>
                                    </div>
                                )}

                                {/* Connected live — kill-switch for approval-gated execution */}
                                {brokerStatus.broker === 'zerodha' && brokerStatus.connected && (
                                    <div className="mt-3 pt-3 border-t border-slate-100 space-y-2">
                                        <div className="flex items-center justify-between text-sm">
                                            <span className="text-slate-500">Live trading</span>
                                            <button
                                                onClick={toggleLiveTrading}
                                                className={`px-2.5 py-1 rounded-full text-[11px] font-bold transition ${
                                                    brokerStatus.settings?.live_trading_enabled
                                                        ? 'bg-emerald-100 text-emerald-700'
                                                        : 'bg-slate-100 text-slate-500'
                                                }`}
                                            >
                                                {brokerStatus.settings?.live_trading_enabled ? 'ON' : 'OFF'}
                                            </button>
                                        </div>
                                        <p className="text-[10px] text-slate-400 leading-relaxed">
                                            Every real order still waits for your approval. The switch is a hard
                                            kill-switch — while OFF, no live order can be placed.
                                        </p>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="text-xs text-slate-400 text-center py-2">Loading broker status…</div>
                        )}
                    </div>

                    {/* Risk settings — toggle between summary and full editor */}
                    {showRiskSettings ? (
                        <RiskSettingsPanel onClose={() => setShowRiskSettings(false)} />
                    ) : (
                        <div className="glass-panel p-5">
                            <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-2">
                                    <Shield size={14} className="text-emerald-500" />
                                    <span className="text-xs font-bold text-slate-700 uppercase tracking-wider">Risk Rules</span>
                                </div>
                                <button
                                    onClick={() => setShowRiskSettings(true)}
                                    className="text-xs text-indigo-500 hover:underline font-medium"
                                >
                                    Customise →
                                </button>
                            </div>
                            <p className="text-xs text-slate-400">
                                All trades pass through your risk rules before executing.
                                Tap <span className="font-medium text-indigo-500">Customise</span> to adjust limits.
                            </p>
                        </div>
                    )}
                </div>
            </div>

            {/* ── Insights tabs: Weekly Review · Signal Accuracy · Portfolios ── */}
            <InsightsTabs />
        </div>
    );
}

/* ── InsightsTabs ─────────────────────────────────────────────────────────── */
function InsightsTabs() {
    const TABS = [
        { id: 'weekly',   label: 'Weekly Review',    icon: BookOpen },
        { id: 'signals',  label: 'Signal Accuracy',  icon: BarChart3 },
        { id: 'portfolios', label: 'Portfolios',     icon: Briefcase },
    ];
    const [active, setActive] = useState('weekly');

    return (
        <div>
            {/* Tab bar */}
            <div style={{ display: 'flex', gap: 4, marginBottom: 16, borderBottom: '1px solid #e2e8f0', paddingBottom: 0 }}>
                {TABS.map(({ id, label, icon: Icon }) => {
                    const isActive = active === id;
                    return (
                        <button
                            key={id}
                            onClick={() => setActive(id)}
                            style={{
                                display: 'flex', alignItems: 'center', gap: 6,
                                padding: '8px 16px', border: 'none', background: 'none',
                                cursor: 'pointer', fontSize: 13, fontWeight: isActive ? 700 : 500,
                                color: isActive ? '#2563eb' : '#64748b',
                                borderBottom: isActive ? '2px solid #2563eb' : '2px solid transparent',
                                marginBottom: -1,
                                transition: 'all 0.15s',
                            }}
                        >
                            <Icon size={14} />{label}
                        </button>
                    );
                })}
            </div>

            {active === 'weekly'     && <WeeklyReview />}
            {active === 'signals'    && <SignalAccuracy />}
            {active === 'portfolios' && <Portfolios />}
        </div>
    );
}
